import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { LanguageToggle } from "@/components/LanguageToggle";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const HERO = "https://images.pexels.com/photos/28758998/pexels-photo-28758998.jpeg?auto=compress&cs=tinysrgb&w=1200";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const t = useT();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "coach" ? "/coach" : u.role === "admin" ? "/admin" : "/app/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-ink">
        <img src={HERO} alt="Cricketer playing a shot" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
        <div className="relative p-12 h-full flex flex-col justify-between">
          <Logo light />
          <div>
            <h2 className="font-head font-black text-white text-4xl uppercase leading-tight">{t("Welcome back to")}<br /><span className="text-brand">{t("the middle.")}</span></h2>
            <p className="text-slate-300 mt-3 max-w-sm">{t("Pick up where you left off — your season is waiting.")}</p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 bg-slate-100">
        <form onSubmit={submit} className="w-full max-w-sm ct-card p-8 fade-up" data-testid="login-form">
          <div className="flex items-center justify-between mb-6">
            <div className="lg:hidden"><Logo /></div>
            <div className="ml-auto"><LanguageToggle /></div>
          </div>
          <h1 className="font-head font-bold text-3xl mb-1">{t("Log in")}</h1>
          <p className="text-slate-500 text-sm mb-6">{t("Enter your details to continue.")}</p>
          {error && <div className="mb-4 text-sm text-brand bg-brand/10 rounded-lg p-3" data-testid="login-error">{error}</div>}
          <div className="space-y-4">
            <div>
              <Label htmlFor="email">{t("Email")}</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" className="mt-1" placeholder="you@club.com" />
            </div>
            <div>
              <Label htmlFor="password">{t("Password")}</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" className="mt-1" placeholder="••••••" />
            </div>
          </div>
          <Button type="submit" disabled={busy} className="w-full mt-6 bg-brand hover:bg-brand-dark h-11" data-testid="login-submit">
            {busy ? "…" : t("Log in")}
          </Button>
          <p className="text-center text-sm text-slate-500 mt-4">
            {t("New here?")} <Link to="/register" className="text-brand font-semibold" data-testid="go-register">{t("Create an account")}</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
